/**
 * /admin/networker-vision-images
 *
 * One-click bulk paste: per-category textarea (one URL per line),
 * preview thumbnails inline, save updates the
 * `networker_vision_images` Mongo collection. PerformanceVisionSetup
 * fetches /api/admin/networker-vision/images on mount so changes go
 * live without a deploy.
 *
 * Auth: PUT requires admin JWT, GET is public so the wizard can read.
 */

import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowLeft, Save, Check, AlertCircle } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;

const CATEGORIES = [
  { id: "leadership", label: "Leadership" },
  { id: "team",       label: "Team Growth" },
  { id: "income",     label: "Income & Wealth" },
  { id: "presence",   label: "Confidence & Presence" },
  { id: "freedom",    label: "Freedom Lifestyle" },
  { id: "luxury",     label: "Luxury & Lifestyle" },
];

function getToken() { return localStorage.getItem("reclaim_token") || ""; }

export default function AdminNetworkerVisionImages() {
  const navigate = useNavigate();
  const [data, setData] = useState({});       // { catId: { urls: [], updated_at } }
  const [draft, setDraft] = useState({});     // { catId: textareaValue }
  const [saving, setSaving] = useState({});   // { catId: 'idle'|'saving'|'saved'|'error' }
  const [loading, setLoading] = useState(true);

  // ── Fetch on mount ──────────────────────────────────────────────
  useEffect(() => {
    (async () => {
      try {
        const r = await fetch(`${API}/api/admin/networker-vision/images`);
        const j = await r.json();
        setData(j.categories || {});
        const d = {};
        Object.entries(j.categories || {}).forEach(([k, v]) => {
          d[k] = (v.urls || []).join("\n");
        });
        setDraft(d);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const handleSave = async (cat) => {
    setSaving((s) => ({ ...s, [cat]: "saving" }));
    const urls = (draft[cat] || "")
      .split("\n").map((l) => l.trim()).filter(Boolean);
    try {
      const r = await fetch(`${API}/api/admin/networker-vision/images/${cat}`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${getToken()}`,
        },
        body: JSON.stringify({ urls }),
      });
      if (!r.ok) throw new Error(await r.text());
      const j = await r.json();
      setData((d) => ({ ...d, [cat]: { urls: j.urls, updated_at: j.updated_at } }));
      setDraft((d) => ({ ...d, [cat]: (j.urls || []).join("\n") }));
      setSaving((s) => ({ ...s, [cat]: "saved" }));
      setTimeout(() => setSaving((s) => ({ ...s, [cat]: "idle" })), 1800);
    } catch (e) {
      console.error(e);
      setSaving((s) => ({ ...s, [cat]: "error" }));
      setTimeout(() => setSaving((s) => ({ ...s, [cat]: "idle" })), 2400);
    }
  };

  const previewUrls = (cat) => (draft[cat] || "")
    .split("\n").map((l) => l.trim()).filter((u) => u.startsWith("http"));

  if (loading) {
    return (
      <div className="min-h-screen bg-[#050402] flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-amber-500/30 border-t-amber-500 rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#050402] text-white"
         data-testid="admin-networker-vision-page">
      <div className="max-w-4xl mx-auto px-6 py-8">
        <button
          onClick={() => navigate("/admin")}
          className="flex items-center gap-2 text-white/55 hover:text-white mb-5"
        >
          <ArrowLeft className="w-4 h-4" />
          <span className="text-[11px] uppercase tracking-[0.3em]">Admin</span>
        </button>
        <h1 className="text-2xl font-semibold mb-1">Networker Vision Images</h1>
        <p className="text-white/45 text-sm mb-8">
          One URL per line. Pexels / Unsplash hot-link URLs are best.
          Saves go live immediately for the Performance Vision Setup wizard.
        </p>

        <div className="space-y-8">
          {CATEGORIES.map((cat) => {
            const state = saving[cat.id] || "idle";
            const urls  = previewUrls(cat.id);
            const updated = data[cat.id]?.updated_at;
            return (
              <div key={cat.id}
                   className="rounded-xl p-5"
                   style={{
                     background: "linear-gradient(135deg, rgba(15,11,8,0.92), rgba(8,6,4,0.92))",
                     border: "1px solid rgba(217,165,90,0.16)",
                   }}>
                <div className="flex items-center justify-between mb-3">
                  <div>
                    <h2 className="text-amber-200/90 text-base font-semibold">{cat.label}</h2>
                    <p className="text-white/40 text-xs mt-0.5">
                      {urls.length} URL{urls.length !== 1 ? "s" : ""}
                      {updated && ` · last updated ${new Date(updated).toLocaleString()}`}
                    </p>
                  </div>
                  <button
                    onClick={() => handleSave(cat.id)}
                    disabled={state === "saving"}
                    data-testid={`nv-save-${cat.id}`}
                    className="inline-flex items-center gap-2 px-4 py-2 rounded-full text-[11px] uppercase tracking-[0.22em] transition-all"
                    style={{
                      background: state === "saved"
                        ? "rgba(16,185,129,0.18)"
                        : state === "error"
                          ? "rgba(244,63,94,0.18)"
                          : "linear-gradient(135deg, rgba(217,165,90,0.92), rgba(180,120,60,0.95))",
                      border: `1px solid ${state === "saved" ? "rgba(16,185,129,0.45)"
                                          : state === "error" ? "rgba(244,63,94,0.45)"
                                          : "rgba(217,165,90,0.45)"}`,
                      color: state === "saved" ? "rgb(110,231,183)"
                             : state === "error" ? "rgb(253,164,175)"
                             : "white",
                    }}
                  >
                    {state === "saved"   && <><Check className="w-3.5 h-3.5" /> Saved</>}
                    {state === "saving"  && <><span className="w-3.5 h-3.5 rounded-full border border-white/60 border-t-transparent animate-spin" /> Saving</>}
                    {state === "error"   && <><AlertCircle className="w-3.5 h-3.5" /> Retry</>}
                    {state === "idle"    && <><Save className="w-3.5 h-3.5" /> Save</>}
                  </button>
                </div>

                <textarea
                  value={draft[cat.id] || ""}
                  onChange={(e) => setDraft((d) => ({ ...d, [cat.id]: e.target.value }))}
                  data-testid={`nv-textarea-${cat.id}`}
                  className="w-full h-40 p-3 rounded-lg bg-black/40 border border-white/10 text-white/85 text-xs font-mono focus:outline-none focus:border-amber-500/45"
                  placeholder="https://images.pexels.com/photos/.../pexels-photo.jpeg&#10;https://images.unsplash.com/photo-..."
                  spellCheck={false}
                />

                {urls.length > 0 && (
                  <div className="mt-4 grid grid-cols-3 sm:grid-cols-6 gap-2">
                    {urls.slice(0, 12).map((u, i) => (
                      <div key={u + i}
                           className="relative rounded overflow-hidden"
                           style={{ aspectRatio: "1/1", background: "#0E0B08" }}>
                        <img src={u} alt="" className="w-full h-full object-cover"
                             onError={(e) => { e.currentTarget.style.opacity = 0.2; }} />
                      </div>
                    ))}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
